/**
 * Named enums that mirror the core-api enum definitions.
 *
 * The generated types.gen.ts only contains numeric values because the OpenAPI spec
 * doesn't include enum names. This file provides human-readable names that match
 * the source enums in services/core-api/src/v4/features/projects/enums/
 *
 * These are kept in sync with the source enums manually.
 */

import type {
  KindOfResearch as KindOfResearchType,
  ResearchConductType as ResearchConductTypeType,
  TrustLevel as TrustLevelType,
} from './core-api/types.gen';

/**
 * Named version of TrustLevel enum.
 * Source: services/core-api/src/v4/shared/enums/trust-level.enum.ts
 */
export const TrustLevel = {
  FLAGGED: 0,
  PENDING: 50,
  APPROVED: 95,
  TRUSTED: 100,
} as const satisfies Record<string, TrustLevelType>;

export type TrustLevel = TrustLevelType;

/**
 * Named version of ResearchConductType enum.
 * Source: services/core-api/src/v4/shared/enums/research-conduct-type.enum.ts
 */
export const ResearchConductType = {
  IN_PERSON: 1,
  REMOTE: 2,
} as const satisfies Record<string, ResearchConductTypeType>;

export type ResearchConductType = ResearchConductTypeType;

/**
 * Named version of KindOfResearch enum.
 * Source: services/core-api/src/v4/features/projects/enums/kind-of-research.enum.ts
 */
export const KindOfResearch = {
  ONE_ON_ONE: 1,
  FOCUS_GROUP: 2,
  /** @deprecated */
  MULTI_DAY_STUDY: 3,
  UNMODERATED_STUDY: 4,
  /** @deprecated */
  PHONE_INTERVIEW: 5,
  /** @deprecated */
  USABILITY_TESTING: 6,
  IN_RESPONDENT_HOME: 8,
  SURVEY: 9,
  DIARY_STUDY: 10,
  IN_RESPONDENT_OFFICE: 11,
  AI_MODERATED_INTERVIEW: 12,
} as const satisfies Record<string, KindOfResearchType>;

export type KindOfResearch = KindOfResearchType;

export const KindOfResearchLabels = {
  [KindOfResearch.ONE_ON_ONE]: 'One-on-one',
  [KindOfResearch.FOCUS_GROUP]: 'Focus group',
  [KindOfResearch.MULTI_DAY_STUDY]: 'Multi-day study',
  [KindOfResearch.UNMODERATED_STUDY]: 'Unmoderated study',
  [KindOfResearch.PHONE_INTERVIEW]: 'Phone interview',
  [KindOfResearch.USABILITY_TESTING]: 'Usability testing',
  [KindOfResearch.IN_RESPONDENT_HOME]: 'In-respondent home',
  [KindOfResearch.SURVEY]: 'Survey',
  [KindOfResearch.DIARY_STUDY]: 'Diary study',
  [KindOfResearch.IN_RESPONDENT_OFFICE]: 'In-respondent office',
  [KindOfResearch.AI_MODERATED_INTERVIEW]: 'AI moderated interview',
} as const;

// services/core-api/src/v4/shared/enums/participant-audience-type.enum.ts
export const ParticipantAudienceType = {
  B2B: 1, // = 'Industry Professionals'
  B2C: 2, // = 'General Population'
} as const;

export type ParticipantAudienceType =
  (typeof ParticipantAudienceType)[keyof typeof ParticipantAudienceType];

/**
 * Attribute key enum for project matching attributes.
 */
export const AttributeKey = {
  GENDER: 'gender',
  ETHNICITY: 'ethnicity',
  HOUSEHOLD_INCOME: 'householdIncome',
  EDUCATION_LEVEL: 'educationLevel',
  RELATIONSHIP_STATUS: 'relationshipStatus',
  LANGUAGES: 'languages',
  NUMBER_OF_CHILDREN: 'numberOfChildren',
  NUMBER_OF_CHILDREN_LIVE_AT_HOME: 'numberOfChildrenLiveAtHome',
  CHILDREN_BIRTH_YEARS: 'childrenBirthYears',
  LIVING_SITUATION: 'livingSituation',
  PETS: 'pets',
  HOME_OWNER: 'homeOwner',
  DISABILITY_TYPE: 'disabilityType',
  NATIONALITY: 'nationality',
  AREA_TYPE: 'areaType',
  MILITARY_SERVICE_STATUS: 'militaryServiceStatus',
  WORK_SETTING: 'workSetting',
  EMPLOYMENT_STATUS: 'employmentStatuses',
  INDUSTRY: 'industry',
  COMPANY_SIZE: 'companySize',
  SENIORITY_LEVEL: 'seniorityLevel',
  JOB_FUNCTIONS: 'jobFunctions',
  CLOUD_PLATFORMS: 'cloudPlatforms',
  CODING_AI_TOOLS: 'codingAITools',
  DESIGN_PROTOTYPING_TOOLS: 'designPrototypingTools',
  FOOD_DELIVERY_SERVICES: 'foodDeliveryServices',
  HEALTH_WELLNESS_APPS: 'healthWellnessApps',
  ONLINE_BANKING_TOOLS: 'onlineBankingTools',
  ONLINE_LEARNING_PLATFORMS: 'onlineLearningPlatforms',
  ONLINE_MARKETPLACE_PLATFORMS: 'onlineMarketplacePlatforms',
  ONLINE_TRAVEL_BOOKING: 'onlineTravelBooking',
  PRODUCTIVITY_SOFTWARE: 'productivitySoftware',
  SMART_HOME_DEVICES: 'smartHomeDevices',
  SOCIAL_NETWORK_PLATFORMS: 'socialNetworkPlatforms',
  STREAMING_SERVICES: 'streamingServices',
  VEHICLE_BRANDS: 'vehicleBrands',
  ONLINE_SHOPPING_FREQUENCY: 'onlineShoppingFrequency',
  WEBCAM: 'webcam',
  MOBILE_PHONE_WITH_INTERNET: 'mobilePhoneWithInternet',
  HEADSET_WITH_MICROPHONE: 'headsetWithMicrophone',
  PROGRAMMING_LANGUAGES: 'programmingLanguages',
  BROWSERS: 'browsers',
  SMARTPHONE_OS: 'smartphoneOS',
  TABLET_OS: 'tabletOS',
  COMPUTER_OS: 'computerOS',
  ASSISTIVE_TECHNOLOGIES: 'assistiveTechnologies',
} as const;

export type AttributeKey = (typeof AttributeKey)[keyof typeof AttributeKey];

export const SOURCE_OPTIONS = [
  'Friend or Colleague Referred Me',
  'Former Employer',
  'Search Engine (e.g. Google)',
  'Article or Blog Post',
  'Respondent Contacted Me',
  'Other',
] as const;

export type SourceOption = (typeof SOURCE_OPTIONS)[number];
